// AdvertisingSystem.js - Marketing campaigns that affect demand and relations
// Spend money -> increase route demand -> improve airport relations

// ============================================
// CAMPAIGN TYPES
// ============================================

export const CAMPAIGN_TYPES = {
  LOCAL_AWARENESS: {
    id: 'local_awareness',
    name: 'Local Awareness',
    description: 'Billboard and local media campaign targeting a single airport market.',
    costPerMonth: 200000 * 100,    // $200K/month
    duration: 1,                    // 1 month minimum
    effects: {
      demandBoost: 0.15,           // +15% demand to/from target
      relationsGain: 2,            // +2 relations at target airport
      scope: 'single',             // Affects one airport
    },
  },

  REGIONAL_BLITZ: {
    id: 'regional_blitz',
    name: 'Regional Blitz',
    description: 'Coordinated campaign across multiple markets in a region.',
    costPerMonth: 500000 * 100,    // $500K/month
    duration: 2,                    // 2 month minimum
    effects: {
      demandBoost: 0.10,           // +10% demand (spread thinner)
      relationsGain: 1,            // +1 relations at each airport
      scope: 'region',             // Affects all airports in region
    },
  },

  BRAND_CAMPAIGN: {
    id: 'brand_campaign',
    name: 'Brand Campaign',
    description: 'National brand-building through digital and TV advertising.',
    costPerMonth: 1000000 * 100,   // $1M/month
    duration: 3,                    // 3 month minimum
    effects: {
      demandBoost: 0.05,           // +5% demand everywhere
      relationsGain: 0.5,          // +0.5 relations everywhere
      reputationBoost: 50,         // +50 rep per month
      scope: 'national',           // Affects all your routes
    },
  },

  LOYALTY_PROGRAM: {
    id: 'loyalty_program',
    name: 'Loyalty Program Launch',
    description: 'Launch frequent flyer program to drive repeat bookings.',
    costPerMonth: 750000 * 100,    // $750K/month
    duration: 6,                    // 6 month commitment
    effects: {
      demandBoost: 0.20,           // +20% demand on existing routes
      loadFactorBoost: 0.08,       // +8% load factor
      scope: 'existing_routes',    // Only routes you already fly
    },
  },

  CRISIS_PR: {
    id: 'crisis_pr',
    name: 'Crisis PR',
    description: 'Damage control campaign to recover from incidents.',
    costPerMonth: 300000 * 100,    // $300K/month
    duration: 1,                    // 1 month
    effects: {
      relationsRecovery: 5,        // +5 relations recovery at negative airports
      reputationRecovery: 100,     // +100 rep recovery
      scope: 'damaged',            // Only affects airports with negative relations
    },
  },
};

// ============================================
// REGIONS FOR REGIONAL CAMPAIGNS
// ============================================

const REGIONS = {
  PACIFIC_NORTHWEST: ['KPDX', 'KSEA', 'KGEG', 'KEUG', 'KMFR', 'KBOI', 'KRDM'],
  CALIFORNIA: ['KSFO', 'KOAK', 'KSJC', 'KSMF', 'KLAX', 'KSAN'],
  MOUNTAIN: ['KBZN', 'KJAC', 'KIDA', 'KSLC', 'KRNO', 'KDEN', 'KCOS', 'KASE'],
  SOUTHWEST: ['KPHX', 'KLAS'],
  MIDWEST: ['KORD', 'KMSP', 'KDTW'],
  SOUTH: ['KATL', 'KBNA', 'KCLT'],
  ALASKA: ['PANC', 'PAFA', 'PADQ'],
};

// ============================================
// ADVERTISING STATE
// ============================================

export function createAdvertisingState() {
  return {
    activeCampaigns: [],
    totalMonthlySpend: 0,
    history: [],
  };
}

// ============================================
// CAMPAIGN ACTIONS
// ============================================

export function launchCampaign(state, campaignType, target, cash) {
  const template = CAMPAIGN_TYPES[campaignType];
  if (!template) {
    return { success: false, error: 'Unknown campaign type' };
  }
  
  // Check if can afford first month
  if (cash < template.costPerMonth) {
    return { success: false, error: 'Insufficient funds for campaign' };
  }
  
  // Validate target
  if (template.effects.scope === 'single' && !target) {
    return { success: false, error: 'Must specify target airport' };
  }
  if (template.effects.scope === 'region' && !REGIONS[target]) {
    return { success: false, error: 'Invalid region' };
  }
  
  const campaign = {
    id: `campaign-${Date.now()}`,
    type: campaignType,
    name: template.name,
    target,
    targetAirports: getTargetAirports(template.effects.scope, target),
    costPerMonth: template.costPerMonth,
    monthsRemaining: template.duration,
    monthsPaid: 1,
    startedAt: Date.now(),
    effects: template.effects,
  };
  
  const newState = {
    ...state,
    activeCampaigns: [...state.activeCampaigns, campaign],
    totalMonthlySpend: state.totalMonthlySpend + template.costPerMonth,
  };
  
  return {
    success: true,
    state: newState,
    cost: template.costPerMonth,
    campaign,
    message: `${template.name} campaign launched${target ? ` targeting ${target}` : ''}.`,
  };
}

export function cancelCampaign(state, campaignId) {
  const campaign = state.activeCampaigns.find(c => c.id === campaignId);
  if (!campaign) {
    return { success: false, error: 'Campaign not found' };
  }
  
  // Early cancellation penalty = 1 month cost
  const penalty = campaign.monthsRemaining > 0 ? campaign.costPerMonth : 0;
  
  const newState = {
    ...state,
    activeCampaigns: state.activeCampaigns.filter(c => c.id !== campaignId),
    totalMonthlySpend: state.totalMonthlySpend - campaign.costPerMonth,
    history: [...state.history, {
      ...campaign,
      endedAt: Date.now(),
      endReason: 'cancelled',
    }],
  };
  
  return {
    success: true,
    state: newState,
    penalty,
    message: `${campaign.name} cancelled.${penalty > 0 ? ` Cancellation fee: $${formatMoney(penalty)}` : ''}`,
  };
}

export function processMonthlyAdvertising(state, relationsState, worldState) {
  let totalCost = 0;
  const effects = [];
  const completedCampaigns = [];
  
  const updatedCampaigns = state.activeCampaigns.map(campaign => {
    totalCost += campaign.costPerMonth;
    
    // Calculate and record effects
    const campaignEffects = calculateCampaignEffects(campaign, relationsState, worldState);
    effects.push({
      campaign: campaign.name,
      target: campaign.target,
      ...campaignEffects,
    });
    
    // Decrement remaining months
    const updated = {
      ...campaign,
      monthsRemaining: campaign.monthsRemaining - 1,
      monthsPaid: campaign.monthsPaid + 1,
    };
    
    if (updated.monthsRemaining <= 0) {
      completedCampaigns.push(updated);
    }
    
    return updated;
  });
  
  // Remove completed campaigns
  const activeCampaigns = updatedCampaigns.filter(c => c.monthsRemaining > 0);
  
  const newState = {
    ...state,
    activeCampaigns,
    totalMonthlySpend: activeCampaigns.reduce((sum, c) => sum + c.costPerMonth, 0),
    history: [
      ...state.history,
      ...completedCampaigns.map(c => ({
        ...c,
        endedAt: Date.now(),
        endReason: 'completed',
      })),
    ],
  };
  
  return {
    state: newState,
    totalCost,
    effects,
    completedCampaigns,
  };
}

function calculateCampaignEffects(campaign, relationsState, worldState) {
  const effects = campaign.effects;
  const result = {
    demandBoosts: [],
    relationsGains: [],
    reputationGain: 0,
  };
  
  // Demand boost
  if (effects.demandBoost) {
    campaign.targetAirports.forEach(airport => {
      result.demandBoosts.push({
        airport,
        boost: effects.demandBoost,
      });
    });
  }
  
  // Relations gain
  if (effects.relationsGain) {
    campaign.targetAirports.forEach(airport => {
      result.relationsGains.push({
        airport,
        gain: effects.relationsGain,
      });
    });
  }
  
  // Relations recovery (crisis PR)
  if (effects.relationsRecovery) {
    // Only apply to airports with negative relations
    Object.entries(relationsState?.airports || {}).forEach(([code, airport]) => {
      const playerRelation = airport.loyalty?.PLAYER || 0;
      if (playerRelation < 0) {
        result.relationsGains.push({
          airport: code,
          gain: effects.relationsRecovery,
        });
      }
    });
  }
  
  // Reputation
  if (effects.reputationBoost) {
    result.reputationGain = effects.reputationBoost;
  }
  if (effects.reputationRecovery) {
    result.reputationGain = effects.reputationRecovery;
  }
  
  return result;
}

function getTargetAirports(scope, target) {
  switch (scope) {
    case 'single':
      return target ? [target] : [];
    case 'region':
      return REGIONS[target] || [];
    case 'national':
      return Object.values(REGIONS).flat();
    case 'existing_routes':
      // This would be populated from worldState routes
      return [];
    case 'damaged':
      // This would be populated from relationsState
      return [];
    default:
      return [];
  }
}

// ============================================
// DEMAND MODIFIER CALCULATOR
// ============================================

export function getAdvertisingDemandModifier(advertisingState, airportCode) {
  // Calculate total demand boost for an airport from all active campaigns
  let modifier = 1.0;
  
  advertisingState.activeCampaigns.forEach(campaign => {
    if (campaign.targetAirports.includes(airportCode)) {
      modifier += campaign.effects.demandBoost || 0;
    }
  });
  
  return modifier;
}

export function getAdvertisingRelationsBoost(advertisingState, airportCode) {
  // Calculate relations boost from advertising
  let boost = 0;
  
  advertisingState.activeCampaigns.forEach(campaign => {
    if (campaign.targetAirports.includes(airportCode)) {
      boost += campaign.effects.relationsGain || 0;
    }
  });
  
  return boost;
}

// ============================================
// UTILITY
// ============================================

function formatMoney(cents) {
  if (cents >= 100000000) {
    return `${(cents / 100000000).toFixed(1)}M`;
  } else if (cents >= 100000) {
    return `${(cents / 100000).toFixed(0)}K`;
  }
  return (cents / 100).toLocaleString();
}

// ============================================
// EXPORTS
// ============================================

export default {
  CAMPAIGN_TYPES,
  REGIONS,
  createAdvertisingState,
  launchCampaign,
  cancelCampaign,
  processMonthlyAdvertising,
  getAdvertisingDemandModifier,
  getAdvertisingRelationsBoost,
};
